/**
 * Pagination math helpers (pure functions).
 * Keep them small and reusable.
 */

export type PaginationParams = {
  total: number; // total items count (>= 0)
  perPage: number; // items per page (>= 1)
};

/**
 * Calculate total pages from total items and perPage.
 */
export function pagesFromTotal(total: number, perPage: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0;
  if (!Number.isFinite(perPage) || perPage <= 0) return 0;
  return Math.max(1, Math.ceil(total / perPage));
}

/** Clamp page into [1, pages] (returns 0 if pages is 0) */
export function clampPage(page: number, pages: number): number {
  if (pages <= 0) return 0;
  if (!Number.isFinite(page)) return 1;
  if (page < 1) return 1;
  if (page > pages) return pages;
  return page;
}

/**
 * Get offset/limit pair for data slicing/fetching.
 */
export function getOffsetLimit(page: number, perPage: number): { offset: number; limit: number } {
  const safePerPage = perPage > 0 ? perPage : 1;
  const safePage = page > 0 ? page : 1;
  const offset = (safePage - 1) * safePerPage;
  return { offset, limit: safePerPage };
}

/**
 * Build contiguous page range around current page.
 * Ensures numbers are within [1, pages].
 */
export function buildPagesRange(currentPage: number, pages: number, pagesShow: number = 10): number[] {
  if (pages <= 0) return [];
  const realOffset = Math.max(0, Math.floor(pagesShow) - 1);
  const min = Math.max(1, Math.min(currentPage, pages));
  const minPage = pages - min < realOffset ? Math.max(1, pages - realOffset) : Math.max(1, min);
  const maxPage = Math.min(pages, minPage + realOffset);
  const range: number[] = [];
  for (let i = minPage; i <= maxPage; i++) range.push(i);
  return range;
}
